import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Form, Tabs, type TabsProps } from 'antd';

import { useTpoGenConfig } from '../../context/CustomerConfigContext';
import { buildEventPayload } from '../../utils/buildEventPayload';
import { usePlanDetailsContext } from '../context/PlanDetailsContext';
import PromoTab from '../Promo/PromoTab';
import useCreateEventMutation from './hooks/useCreateEventMutation';
import type { CreateEventPayload } from './hooks/useCreateEventMutation';
import useGetEventData from './hooks/useGetEventData';
import useEventDirtyState from './hooks/ui/useEventDirtyState';
import EventsSection from './EventsSection';
import UnsavedChangesModal from './components/UnsavedChangesModal';
import DeletePromoModal from './components/DeletePromoModal';
import NoPromosModal from './components/NoPromosModal';

const ADD_PROMO_TAB_KEY = 'add-promo';
const editPromoTabKey = (promoId: string) => `edit-promo-${promoId}`;

type EventsViewProps = {
    planId: string;
};

type EditPromoEntry = {
    promoId: string;
    label: string;
};

const EventsView = ({ planId }: EventsViewProps) => {
    const tpoGenConfig = useTpoGenConfig();
    const { eventDetailsFields, promoDetailsFields } = tpoGenConfig;
    const {
        setActiveView,
        editEventId,
        setEditEventId,
        editPromoId,
        setEditPromoId,
        autoAddPromo,
        setAutoAddPromo,
        setHasUnsavedNoPromoEvent,
    } = usePlanDetailsContext();

    const isEditMode = !!editEventId;
    const [eventForm] = Form.useForm();
    const [eventId, setEventId] = useState<string | null>(editEventId);
    const [promoTabCount, setPromoTabCount] = useState(0);
    const [savedPromoIds, setSavedPromoIds] = useState<Record<string, string>>({});
    const [promoDirtyMap, setPromoDirtyMap] = useState<Record<string, boolean>>({});
    const [activeTabKey, setActiveTabKey] = useState('1');
    const [openEditPromos, setOpenEditPromos] = useState<EditPromoEntry[]>([]);
    const [removedTabs, setRemovedTabs] = useState<Set<string>>(new Set());
    const [pendingCloseTabKey, setPendingCloseTabKey] = useState<string | null>(null);
    const [showUnsavedModal, setShowUnsavedModal] = useState(false);
    const [showNoPromosModal, setShowNoPromosModal] = useState(false);
    const [activeEventView, setActiveEventView] = useState<'event-details' | 'promo-details'>('event-details');
    const [deletePromoId, setDeletePromoId] = useState<string | null>(null);

    const rawEventFormValues = Form.useWatch([], eventForm);
    const eventFormValues: Record<string, unknown> = useMemo(() => rawEventFormValues ?? {}, [rawEventFormValues]);

    const {
        formValues: fetchedEventFormValues,
        eventPromos,
        isPending: isEventDataPending,
    } = useGetEventData(isEditMode ? editEventId : null);

    const { mutate: mutateEvent, isPending: isEventMutationPending } = useCreateEventMutation({
        onSuccess: (data) => {
            const savedEventId = data?.event_id ?? data?.eventId ?? '';
            setEventId(savedEventId);
            setEditEventId(savedEventId);
            setActiveEventView('event-details');
        },
    });

    const isPromoTab = activeTabKey !== '1' && activeTabKey !== ADD_PROMO_TAB_KEY;

    const eventFieldConfigs = useMemo(
        () => [...eventDetailsFields, ...promoDetailsFields],
        [eventDetailsFields, promoDetailsFields],
    );

    const { isDirty: isEventFormDirty, canSave: canSaveEvent } = useEventDirtyState({
        fieldConfigs: eventFieldConfigs,
        formValues: eventFormValues,
        fetchedFormValues: fetchedEventFormValues ?? null,
        isEditMode,
    });

    const isEventSaved = !!eventId;
    const hasAnySavedPromo = eventPromos.length > 0 || Object.keys(savedPromoIds).length > 0;
    const isActivePromoDirty = isPromoTab && !!promoDirtyMap[activeTabKey];
    const isEventSaveDisabled = !canSaveEvent || (!isEditMode && isEventSaved);

    const isClosingTab = !!pendingCloseTabKey;
    const modalTitle = isClosingTab
        ? tpoGenConfig.labels.modals.unsavedPromoChanges
        : isPromoTab
        ? tpoGenConfig.labels.modals.unsavedPromoChanges
        : tpoGenConfig.labels.modals.unsavedEventChanges;
    const modalDescription =
        isClosingTab || isPromoTab
            ? tpoGenConfig.labels.modals.unsavedPromoDescription
            : tpoGenConfig.labels.modals.unsavedEventDescription;

    const navigateToCalendar = useCallback(() => {
        setEditEventId(null);
        setEditPromoId(null);
        setAutoAddPromo(false);
        setActiveView('calendar');
    }, [setEditEventId, setEditPromoId, setAutoAddPromo, setActiveView]);

    const handleAddPromo = useCallback(() => {
        const newIndex = promoTabCount + 1;
        const newTabKey = `promo-${newIndex}`;
        setPromoTabCount(newIndex);
        setActiveTabKey(newTabKey);
    }, [promoTabCount]);

    const handleClose = () => {
        setAutoAddPromo(false);
        if (isEventSaved && !hasAnySavedPromo) {
            setShowNoPromosModal(true);
            return;
        }
        if (isPromoTab ? isActivePromoDirty : isEventFormDirty) {
            setShowUnsavedModal(true);
        } else {
            navigateToCalendar();
        }
    };

    const handleEventSave = async () => {
        try {
            await eventForm.validateFields();
            const formValues = eventForm.getFieldsValue();
            const configPayload = buildEventPayload(tpoGenConfig.entityAttributes, eventDetailsFields, formValues);
            const products = (formValues['dim1-material_group'] as string[]) ?? [];

            mutateEvent({
                ...configPayload,
                plan_id: planId,
                event_id: eventId,
                lowest_dim_attr_id: products.map(Number),
            } as CreateEventPayload);
        } catch (errorInfo) {
            // eslint-disable-next-line no-console
            console.error('Validation failed:', errorInfo);
        }
    };

    const handleDeletePromo = useCallback((promoId: string) => {
        setDeletePromoId(promoId);
    }, []);

    const handlePromoSaved = useCallback((tabKey: string, promotionId: string) => {
        setSavedPromoIds((prev) => ({ ...prev, [tabKey]: promotionId }));
    }, []);

    const handlePromoDirtyChange = useCallback((tabKey: string, isDirty: boolean) => {
        setPromoDirtyMap((prev) => ({ ...prev, [tabKey]: isDirty }));
    }, []);

    const removeTab = useCallback(
        (targetKey: string) => {
            if (targetKey.startsWith('edit-promo-')) {
                const promoId = targetKey.replace('edit-promo-', '');
                setOpenEditPromos((prev) => prev.filter((e) => e.promoId !== promoId));
                if (editPromoId === promoId) {
                    setEditPromoId(null);
                }
            } else {
                setRemovedTabs((prev) => new Set(prev).add(targetKey));
            }
            setPromoDirtyMap((prev) => {
                const next = { ...prev };
                delete next[targetKey];
                return next;
            });
            setSavedPromoIds((prev) => {
                const next = { ...prev };
                delete next[targetKey];
                return next;
            });
            if (activeTabKey === targetKey) {
                setActiveTabKey('1');
            }
        },
        [activeTabKey, editPromoId, setEditPromoId],
    );

    const handleTabChange = useCallback(
        (key: string) => {
            if (key === ADD_PROMO_TAB_KEY) {
                handleAddPromo();
                return;
            }
            setActiveTabKey(key);
        },
        [handleAddPromo],
    );

    const handleTabEdit = useCallback(
        (targetKey: React.MouseEvent | React.KeyboardEvent | string, action: 'add' | 'remove') => {
            if (action === 'add') {
                handleAddPromo();
                return;
            }
            const key = targetKey as string;
            if (promoDirtyMap[key]) {
                setPendingCloseTabKey(key);
                setShowUnsavedModal(true);
                return;
            }
            removeTab(key);
        },
        [handleAddPromo, promoDirtyMap, removeTab],
    );

    const tabItems = useMemo(() => {
        const items: NonNullable<TabsProps['items']> = [
            {
                key: '1',
                label: tpoGenConfig.labels.events.eventDetails,
                closable: false,
                children: (
                    <EventsSection
                        form={eventForm}
                        isEventSaved={isEventSaved}
                        onAddPromo={handleAddPromo}
                        onDeletePromo={handleDeletePromo}
                        isEditMode={isEditMode}
                        fetchedFormValues={fetchedEventFormValues}
                        isEventDataPending={isEventDataPending}
                        eventPromos={eventPromos}
                        activeEventView={activeEventView}
                        onActiveEventViewChange={setActiveEventView}
                    />
                ),
            },
        ];

        for (const entry of openEditPromos) {
            const tabKey = editPromoTabKey(entry.promoId);
            items.push({
                key: tabKey,
                label: entry.label,
                closable: true,
                children: (
                    <PromoTab
                        planId={planId}
                        eventId={eventId ?? ''}
                        instanceId={tabKey}
                        promotionId={entry.promoId}
                        onDirtyChange={(isDirty) => handlePromoDirtyChange(tabKey, isDirty)}
                    />
                ),
            });
        }

        let unsavedTabCount = 0;
        for (let i = 1; i <= promoTabCount; i++) {
            const tabKey = `promo-${i}`;
            if (removedTabs.has(tabKey)) continue;
            const savedId = savedPromoIds[tabKey];

            const matchedPromo = savedId ? eventPromos.find((p) => p.promo_id === savedId) : null;

            let label: string;
            if (matchedPromo) {
                label = matchedPromo.promo_name || `Promo ${eventPromos.length}`;
            } else {
                unsavedTabCount++;
                label = `Promo ${eventPromos.length + unsavedTabCount}`;
            }

            items.push({
                key: tabKey,
                label,
                closable: true,
                children: (
                    <PromoTab
                        planId={planId}
                        eventId={eventId ?? ''}
                        instanceId={tabKey}
                        promotionId={savedId}
                        onPromoSaved={(id) => handlePromoSaved(tabKey, id)}
                        onDirtyChange={(isDirty) => handlePromoDirtyChange(tabKey, isDirty)}
                    />
                ),
            });
        }

        items.push({
            key: ADD_PROMO_TAB_KEY,
            label: tpoGenConfig.labels.promos.addPromoTab,
            closable: false,
            children: <></>,
            disabled: !isEventSaved,
        });

        return items;
    }, [
        promoTabCount,
        planId,
        eventId,
        isEventSaved,
        eventForm,
        handleAddPromo,
        handleDeletePromo,
        handlePromoSaved,
        handlePromoDirtyChange,
        savedPromoIds,
        removedTabs,
        isEditMode,
        openEditPromos,
        fetchedEventFormValues,
        isEventDataPending,
        eventPromos,
        activeEventView,
    ]);

    useEffect(() => {
        if (editPromoId) {
            const promo = eventPromos.find((p) => p.promo_id === editPromoId);
            const label = promo?.promo_name || tpoGenConfig.labels.promos.editPromo;

            setOpenEditPromos((prev) => {
                const alreadyOpen = prev.some((e) => e.promoId === editPromoId);
                if (!alreadyOpen) {
                    return [...prev, { promoId: editPromoId, label }];
                }
                // Update label in case eventPromos loaded after the tab was first opened
                return prev.map((e) => (e.promoId === editPromoId && e.label !== label ? { ...e, label } : e));
            });
            setActiveTabKey(editPromoTabKey(editPromoId));
        }
    }, [editPromoId, eventPromos]);

    useEffect(() => {
        if (autoAddPromo && isEventSaved) {
            handleAddPromo();
            setAutoAddPromo(false);
        }
    }, [autoAddPromo, isEventSaved, handleAddPromo, setAutoAddPromo]);

    useEffect(() => {
        setHasUnsavedNoPromoEvent(isEventSaved && !hasAnySavedPromo);
        return () => setHasUnsavedNoPromoEvent(false);
    }, [isEventSaved, hasAnySavedPromo, setHasUnsavedNoPromoEvent]);

    return (
        <div>
            <div className="flex flex-row justify-between items-center">
                <p className="text-base font-medium">
                    {isEditMode ? tpoGenConfig.labels.events.editEvent : tpoGenConfig.labels.events.addNewEvent}
                </p>
                <div className="flex flex-row gap-x-2">
                    {!isPromoTab && (
                        <Button
                            type="primary"
                            onClick={handleEventSave}
                            disabled={isEventSaveDisabled}
                            loading={isEventMutationPending}
                        >
                            {isEditMode ? tpoGenConfig.labels.events.update : tpoGenConfig.labels.common.save}
                        </Button>
                    )}
                    <Button type="default" onClick={handleClose}>
                        {tpoGenConfig.labels.common.close}
                    </Button>
                </div>
            </div>
            <div>
                <Tabs
                    className="events-view-tabs"
                    type="editable-card"
                    activeKey={activeTabKey}
                    onChange={handleTabChange}
                    onEdit={handleTabEdit}
                    items={tabItems}
                    hideAdd
                />
            </div>
            <UnsavedChangesModal
                open={showUnsavedModal}
                title={modalTitle}
                description={
                    <div className="bg-[#fafafa] rounded-lg p-2">
                        <p className="text-sm text-[#18181b]">{modalDescription}</p>
                    </div>
                }
                onContinueEditing={() => {
                    if (pendingCloseTabKey) {
                        setActiveTabKey(pendingCloseTabKey);
                        setPendingCloseTabKey(null);
                    }
                    setShowUnsavedModal(false);
                }}
                onDiscard={() => {
                    if (pendingCloseTabKey) {
                        removeTab(pendingCloseTabKey);
                        setPendingCloseTabKey(null);
                        setShowUnsavedModal(false);
                    } else {
                        eventForm.resetFields();
                        navigateToCalendar();
                    }
                }}
            />
            <DeletePromoModal
                open={!!deletePromoId}
                onClose={() => setDeletePromoId(null)}
                promoId={deletePromoId}
                promoName={eventPromos.find((p) => p.promo_id === deletePromoId)?.promo_name}
                eventId={eventId ?? ''}
            />
            <NoPromosModal
                open={showNoPromosModal}
                onClose={() => setShowNoPromosModal(false)}
                eventId={eventId}
                onDeleteSuccess={navigateToCalendar}
            />
        </div>
    );
};

EventsView.displayName = 'EventsView';
export default EventsView;
